import { Fragment, type FC, useCallback, useState } from 'react'
import classNames from 'classnames'
import emailSender from '@emailjs/browser'
import { Form, Input } from 'antd'

import { Text } from 'components'
import { MessageIcon, PhoneIcon, FacebookIcon, InstagramIcon, TelegramIcons } from 'assets'

import styles from './ContactUs.module.scss'

const inputRules = [{ required: true, message: 'Please fill this input' }]
const armenianPhoneNumberPattern = /^(?:(\+374)[\s-]?)?(\(?\d{2}\)?[\s-]?)?\d{6}$/
const heroImage = `${process.env.PUBLIC_URL}/contactUs.png`

type TStatus = 'idle' | 'sent' | 'error'

const ContactUs: FC = () => {
  const [form] = Form.useForm()
  const [status, setStatus] = useState<TStatus>('idle')

  const onFinish = useCallback(
    async (values: any) => {
      try {
        await emailSender.send('service_8vkodir', 'template_puat2re', values, { publicKey: '0ZNEMXAVKJ6IIaB76' })
        form.resetFields()
        setStatus('sent')
      } catch {
        setStatus('error')
      }
    },
    [form]
  )

  return (
    <Fragment>
      <section className={styles.hero}>
        <div className={styles.hero__media} aria-hidden>
          <img src={heroImage} alt='' className={styles.hero__image} />
          <div className={styles.hero__overlay} />
        </div>
        <div className={classNames('container', styles.hero__inner)}>
          <Text text='contact' className={styles.hero__eyebrow} />
          <Text text='contactUs' className={styles.hero__title} tagName='h1' />
          <Text text='contactText' className={styles.hero__subtitle} />
        </div>
      </section>

      <div className={styles.page}>
        <div className={classNames('container', styles.grid)}>
          <aside className={styles.info}>
            <Text text='contact' className={styles.info__title} tagName='h2' />
            <Text text='contactText' className={styles.info__text} />

            <a className={styles.info__row} href='mailto:info@engineeringgroup.am'>
              <span className={styles.info__icon}>
                <MessageIcon />
              </span>
              <span className={styles.info__value}>info@engineeringgroup.am</span>
            </a>
            <a className={styles.info__row} href='tel:+37444144199'>
              <span className={styles.info__icon}>
                <PhoneIcon />
              </span>
              <span className={styles.info__value}>+374 44 144 199</span>
            </a>

            <div className={styles.info__socials} aria-hidden>
              <span className={styles.info__social}>
                <FacebookIcon />
              </span>
              <span className={styles.info__social}>
                <InstagramIcon />
              </span>
              <span className={styles.info__social}>
                <TelegramIcons />
              </span>
            </div>
          </aside>

          <div className={styles.formCard}>
            <Text text='contactFormTitle' className={styles.formCard__title} tagName='h2' />

            {status === 'sent' && (
              <div className={classNames(styles.notice, styles.notice_success)}>
                <Text text='contactSuccess' />
              </div>
            )}
            {status === 'error' && (
              <div className={classNames(styles.notice, styles.notice_error)}>
                <Text text='contactError' />
              </div>
            )}

            <Form form={form} onFinish={onFinish} className={styles.form} requiredMark={false} layout='vertical'>
              <div className={styles.form__row}>
                <Form.Item name='name' rules={inputRules} label={<Text text='contactName' emptyTag />}>
                  <Input size='large' />
                </Form.Item>
                <Form.Item name='email' rules={inputRules} label={<Text text='contactEmail' emptyTag />}>
                  <Input size='large' />
                </Form.Item>
              </div>
              <Form.Item
                name='phone'
                label={<Text text='contactPhone' emptyTag />}
                rules={[
                  { pattern: armenianPhoneNumberPattern, message: 'Please input a correct phone number' },
                  ...inputRules,
                ]}
              >
                <Input size='large' />
              </Form.Item>
              <Form.Item name='subject' rules={inputRules} label={<Text text='contactSubject' emptyTag />}>
                <Input size='large' />
              </Form.Item>
              <Form.Item name='message' rules={inputRules} label={<Text text='contactMessage' emptyTag />}>
                <Input.TextArea rows={5} />
              </Form.Item>

              <button type='submit' className={styles.form__button}>
                <Text text='contactSend' />
                <span className={styles.form__button_arrow} aria-hidden />
              </button>
            </Form>
          </div>
        </div>
      </div>
    </Fragment>
  )
}

export default ContactUs
